import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Phone, MapPin } from "lucide-react";
import { WizardFormData } from "@/types/wizard";

interface Step2HomeownerInfoProps {
  formData: WizardFormData;
  updateFormData: (updates: Partial<WizardFormData>) => void;
  isDark: boolean;
}

const Step2HomeownerInfo = ({ formData, updateFormData, isDark }: Step2HomeownerInfoProps) => {
  const inputStyles = isDark
    ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414] rounded-xl focus:ring-2 focus:ring-[#E4683B]"
    : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#d1d1d1,inset_-4px_-4px_8px_#ffffff] rounded-xl focus:ring-2 focus:ring-[#E4683B]";

  const labelStyles = isDark ? "text-white" : "text-gray-900";

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 
          className="text-2xl md:text-3xl font-bold mb-2"
          style={{ color: "#E4683B" }}
        >
          Tell us about yourself
        </h2>
        <p 
          className="text-lg opacity-80"
          style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
        >
          We'll use this information to personalize your ADU assessment
        </p>
      </div>

      <div className="max-w-xl mx-auto space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName" className={labelStyles}>
              <User className="w-4 h-4 inline mr-2" style={{ color: "#E4683B" }} />
              First Name *
            </Label>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(e) => updateFormData({ firstName: e.target.value })}
              placeholder="John"
              className={inputStyles}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName" className={labelStyles}>
              Last Name *
            </Label>
            <Input
              id="lastName"
              value={formData.lastName}
              onChange={(e) => updateFormData({ lastName: e.target.value })}
              placeholder="Smith"
              className={inputStyles}
            />
          </div>
        </div>

        {/* Project Address (pre-filled from step 1) */}
        <div className="space-y-2">
          <Label htmlFor="projectAddress" className={labelStyles}>
            <MapPin className="w-4 h-4 inline mr-2" style={{ color: "#E4683B" }} />
            Project Address
          </Label>
          <Input
            id="projectAddress"
            value={formData.projectAddress || formData.propertyData?.address || ""}
            onChange={(e) => updateFormData({ projectAddress: e.target.value })}
            placeholder="Your property address"
            className={inputStyles}
            readOnly={!!formData.propertyData?.address}
          />
          {formData.propertyData?.address && (
            <p className="text-xs opacity-60" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
              Pre-filled from your property selection
            </p>
          )}
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <Label htmlFor="phone" className={labelStyles}>
            <Phone className="w-4 h-4 inline mr-2" style={{ color: "#E4683B" }} />
            Phone Number *
          </Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => updateFormData({ phone: e.target.value })}
            placeholder="(555) 123-4567"
            className={inputStyles}
          />
          <p className="text-xs opacity-60" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
            We'll send you a verification code via SMS
          </p>
        </div>
      </div>
    </div>
  );
};

export default Step2HomeownerInfo;
